import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import {
  collection,
  query,
  where,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  increment,
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  formatDate,
  getFallbackImageForArticle,
  INDUSTRY_VOICES_THEME,
  SITE_URL,
} from '../../config';
import RichContent from '../common/RichContent';
import BackButton from '../common/BackButton';
import ShareBar from '../common/ShareBar';
import { Spinner } from '../common/LoadingIndicators';

function normaliseToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function isIndustryVoicesArticle(article) {
  const want = normaliseToken(INDUSTRY_VOICES_THEME);
  const category = normaliseToken(article?.category);
  if (category === want) return true;
  const themes = Array.isArray(article?.themes) ? article.themes : [];
  return themes.some((t) => normaliseToken(t) === want);
}

function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasRecordedView = useRef(false);

  const toIso = (value) => {
    if (!value) return undefined;
    if (typeof value === 'string') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    }
    if (value?.toDate) {
      try {
        return value.toDate().toISOString();
      } catch {
        return undefined;
      }
    }
    try {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
    } catch {
      return undefined;
    }
  };

  useEffect(() => {
    let unsub;

    async function load() {
      try {
        const q = query(collection(db, 'posts'), where('slug', '==', slug), limit(1));

        unsub = onSnapshot(
          q,
          (snapshot) => {
            if (!snapshot.empty) {
              const docSnap = snapshot.docs[0];
              const data = docSnap.data();
              const nextArticle = { id: docSnap.id, ...data };
              setArticle(nextArticle);

              // Only record views for published articles.
              if (!hasRecordedView.current && nextArticle.published !== false) {
                hasRecordedView.current = true;
                try {
                  updateDoc(doc(db, 'posts', docSnap.id), {
                    views: increment(1),
                  });
                } catch (err) {
                  // eslint-disable-next-line no-console
                  console.error('Failed to record article view', err);
                }
              }
            } else {
              setArticle(null);
            }
            setLoading(false);
          },
          (err) => {
            // eslint-disable-next-line no-console
            console.error('Error loading article', err);
            setArticle(null);
            setLoading(false);
          }
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error querying article', e);
        setArticle(null);
        setLoading(false);
      }
    }

    load();

    return () => {
      if (unsub) unsub();
    };
  }, [slug]);

  if (loading) {
    return (
      <section className="content-card">
        <p className="secondary-text" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Spinner /> Loading article…
        </p>
      </section>
    );
  }

  if (!article) {
    return (
      <section className="content-card">
        <Helmet>
          <title>Article not found – IGAMINGSPOT</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <h1 className="section-subtitle">Article not found</h1>
        <p className="secondary-text">
          This piece is not available or has been removed by the regulator.
        </p>
      </section>
    );
  }

  if (article.published === false) {
    return (
      <section className="content-card">
        <Helmet>
          <title>Article not published – IGAMINGSPOT</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <h1 className="section-subtitle">Not published yet</h1>
        <p className="secondary-text">
          This post is currently in draft because it needs a real image before publishing.
        </p>
      </section>
    );
  }

  return (
    <article
      className="content-card"
      itemScope
      itemType="https://schema.org/NewsArticle"
    >
      <Helmet>
        <title>{`${article.title} – IGAMINGSPOT`}</title>
        <meta
          name="description"
          content={
            article.excerpt ||
            'Regulated iGaming news article from IGAMINGSPOT covering white-market casino and betting activity.'
          }
        />
        <link
          rel="canonical"
          href={`${SITE_URL}/news/${article.slug || slug}`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta
          property="og:description"
          content={
            article.excerpt ||
            'Regulated iGaming news article from IGAMINGSPOT covering white-market casino and betting activity.'
          }
        />
        <meta
          property="og:url"
          content={`${SITE_URL}/news/${article.slug || slug}`}
        />
        <meta
          property="og:image"
          content={article.imageUrl || getFallbackImageForArticle(article)}
        />
        <meta property="og:site_name" content="IGAMINGSPOT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${article.title} – IGAMINGSPOT`}
        />
        <meta
          name="twitter:description"
          content={
            article.excerpt ||
            'Regulated iGaming news article from IGAMINGSPOT covering white-market casino and betting activity.'
          }
        />
        <meta
          name="twitter:image"
          content={article.imageUrl || getFallbackImageForArticle(article)}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: article.title,
            description:
              article.excerpt ||
              'Regulated iGaming news article from IGAMINGSPOT covering white-market casino and betting activity.',
            image: [article.imageUrl || getFallbackImageForArticle(article)].filter(Boolean),
            datePublished: toIso(article.publishedAt),
            dateModified: toIso(article.updatedAt) || toIso(article.publishedAt),
            author: {
              '@type': 'Organization',
              name: 'IGAMINGSPOT',
            },
            publisher: {
              '@type': 'Organization',
              name: 'IGAMINGSPOT',
              logo: {
                '@type': 'ImageObject',
                url: `${SITE_URL}/logo512.png`,
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `${SITE_URL}/news/${article.slug || slug}`,
            },
          })}
        </script>
      </Helmet>
      <header className="content-card-header">
        <div>
          <div className="section-title">{article.category}</div>
          <h1 className="section-subtitle" itemProp="headline">
            {article.title}
          </h1>
        </div>
        <BackButton fallbackTo="/news" label="Back" />
      </header>
      {(() => {
        const fallbackSrc = getFallbackImageForArticle(article);
        const imgSrc = article.imageUrl || fallbackSrc;
        return (
          imgSrc && (
            <div className="article-hero">
              <img
                key={imgSrc}
                src={imgSrc}
                alt={article.title}
                className="article-hero-image"
                loading="lazy"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  const target = e.target;
                  if (target.dataset.fallbackApplied === 'true') {
                    // eslint-disable-next-line no-param-reassign
                    target.style.display = 'none';
                    return;
                  }
                  // eslint-disable-next-line no-param-reassign
                  target.style.display = '';
                  // eslint-disable-next-line no-param-reassign
                  target.src = fallbackSrc;
                  // eslint-disable-next-line no-param-reassign
                  target.dataset.fallbackApplied = 'true';
                }}
              />
            </div>
          )
        );
      })()}
      <p className="secondary-text">
        Region: <strong>{article.region}</strong> · KPI focus:{' '}
        <strong>{article.kpi}</strong> · Status: {article.sentiment} outlook.
      </p>
      <p className="secondary-text">
        Published{' '}
        <time dateTime={article.publishedAt} itemProp="datePublished">
          {formatDate(article.publishedAt)}
        </time>{' '}
        · Last updated{' '}
        <time dateTime={article.updatedAt} itemProp="dateModified">
          {formatDate(article.updatedAt)}
        </time>{' '}
        · {article.readMinutes} min read.
      </p>
      <p className="news-excerpt" itemProp="description">
        {article.excerpt}
      </p>
      {article.body ? (
        <div className="article-body">
          <RichContent content={article.body} forceAutoFormat={isIndustryVoicesArticle(article)} />
        </div>
      ) : (
        <p className="secondary-text">
          Full article body will be generated by the AI post writer and validated by the
          government content team. Only approved, white-market operators are reflected in
          the underlying statistics.
        </p>
      )}
      <ShareBar
        url={`${SITE_URL}/news/${article.slug || slug}`}
        title={article.title}
        text={article.excerpt}
        label="Share"
        compact
      />
    </article>
  );
}

export default ArticlePage;


